import { gsap } from 'gsap';
import template from './navigationBar.tpl';
import ViewEvent from '@/core/event/viewEvent';
import EventDispatcher from '@/core/event/eventDispatcher';
import ViewportUtility from '@/core/utils/viewport/viewportUtil';
import ContentModel from '@/app/model/contentModel';
import AppStateModel from '@/app/model/appStateModel';
import PersonComparisonModel from '@/app/views/person/model/personComparisonModel';
import Sharer from './sharer';
import styles from './navigation.module.scss';

import lifespanIcon from '@/assets/icons/nav_lifespan.svg?raw';
import regionIcon from '@//assets/icons/nav_regional_comparison.svg?raw';
import personIcon from '@//assets/icons/nav_personal_comparison.svg?raw';
import cohortIcon from '@/assets/icons/nav_cohort.svg?raw';
import LogUtil from '@/core/utils/logUtil';
import { getOGMetaTags, OGMetaTags } from '@/core/utils/sharingDataUtil'

export default class NavigationBar {

    private common: any;
    private sharing: any;
    private data: any;
    private _element: HTMLElement;
    private timeline: gsap.core.Timeline;
    private allowPopup: boolean;
    private current?: HTMLElement;
    private socialMediaWrapper?: HTMLElement | null;
    private socialMediaAnchors?: NodeListOf<HTMLElement>;
    private OGmetaTags: OGMetaTags;
    private nav_bg?: HTMLElement | null;
    private hint_popup?: HTMLElement | null;
    private hint_text?: HTMLElement | null;
    private hint_close_button?: HTMLElement | null;
    private nav_cohort?: HTMLElement | null;
    private nav_person?: HTMLElement | null;
    private nav_region?: HTMLElement | null;
    private nav_hint?: HTMLElement | null;

    constructor(targetNode, views) {

        let icons: any = {};
        icons.regionalcomparison = regionIcon;
        icons.lifespan = lifespanIcon;
        icons.personcomparison = personIcon;
        icons.cohorttable = cohortIcon;

        this.common = ContentModel.instance.getCommonData();
        this.sharing = ContentModel.instance.getShareData();
        this.OGmetaTags = getOGMetaTags();
        this.data = {
            views: views,
            logo: this.common.navigation.logo,
            styles: styles,
            icons: icons,
            hint_text: this.common.navigation.hint.text,
            theme_param: `t=${AppStateModel.themeParam}`,
            sharing_targets: this.sharing.targets,
        };
        this._element = targetNode;


        this.timeline = gsap.timeline({ paused: false });
        this.allowPopup = true;

        this.render();
    }

    navigationClickHandler(navItem) {

        // clear show-tween so we don't see popup later in a different view.
        this.timeline.clear();

        LogUtil.logClass(this, 'navigationClickHandler', navItem.getAttributeNode('data-target').value);

        let nav_target = navItem.getAttributeNode('data-target').value;
        if (nav_target) {
            this.hideHintPopup();
            EventDispatcher.dispatchEvent(new ViewEvent(ViewEvent.LOAD, nav_target));
        }
    }

    socialMediaClickHandler(shareButton) {
        if (Sharer.isActive) {
            this.setMetaValuesOG(); // apply meta tag values

            switch (shareButton.getAttributeNode('data-target').value) {
                case 'link':
                    Sharer.shareToClipboard(shareButton)
                    break;
                /*case 'facebook':
                    Sharer.shareToFacebook(); // connect to FB
                    break;*/
                case 'x':
                    Sharer.shareToX(); // connect to X
                    break;
                case 'linkedin':
                    Sharer.shareToLinkedin(); // connect to LinkedIn
                    break;
            }
        }
    }

    setMetaValuesOG() {
        // needed for sharing (meta-tags used by 3rd party crawlers to scrape info)

        let title: string = ""
        let description: string = ""
        let left: any
        let right: any

        switch (AppStateModel.currentViewID) {
            case 'lifespan':
                title = this.data.views.lifespan.meta.title;
                description = this.data.views.lifespan.meta.description_prefix + Math.round(parseFloat(AppStateModel.getResultsByBirthdayAndGender().p)) + this.data.views.lifespan.meta.description_suffix;
                break;
            case 'cohorttable':
                title = this.data.views.cohorttable.meta.title;
                description = this.data.views.cohorttable.meta.description_prefix + Math.round(parseFloat(AppStateModel.getResultsByBirthdayAndGender().p)) + this.data.views.cohorttable.meta.description_suffix;
                break;
            case 'personcomparison':
                // we want to calculate the diff between 2 people
                left = PersonComparisonModel.getResultsByBirthdayAndGender(PersonComparisonModel.people.left.birthyear, PersonComparisonModel.people.left.gender);
                right = PersonComparisonModel.getResultsByBirthdayAndGender(PersonComparisonModel.people.right.birthyear, PersonComparisonModel.people.right.gender);

                title = this.data.views.personcomparison.meta.title;
                description = this.data.views.personcomparison.meta.description_prefix + Math.floor(Math.abs(left.p - right.p)) + this.data.views.personcomparison.meta.description_suffix;
                break;
            case 'regionalcomparison':
                title = this.data.views.regionalcomparison.meta.title;
                description = this.data.views.regionalcomparison.meta.description_prefix + this.data.views.regionalcomparison.meta.description_suffix;
        }

        // set needed meta-tags
        this.OGmetaTags.url.setAttribute('content', this.getCurrentURL());
        this.OGmetaTags.title.setAttribute('content', title);
        this.OGmetaTags.description.setAttribute('content', description);
        this.OGmetaTags.image.setAttribute('content', this.sharing.image);
    }

    getCurrentURL() {
        return this.sharing.host_url + '#!/' + AppStateModel.currentViewID;
    }

    set enabled(value) {

        this._element.style.opacity = `${(value) ? 1 : 0.5}`;
    }

    get enabled() {
        return false;
    }

    set currentNavItem(id) {

        for (let item of this._element.querySelectorAll('li')) {
            item.classList.remove('active');
        }

        this.current = this._element.querySelector('li.li-' + id) as HTMLElement;
        this.current.className += ' active';

    }

    render() {

        this._element.innerHTML = template(this.data);

        let navi_list = this._element.querySelectorAll('ul#' + styles.navi_list + ' a');

        for (let item of navi_list) {
            item.addEventListener('click', () => this.navigationClickHandler(item));
        }

        this.socialMediaWrapper = this._element.querySelector(`#${styles.social_media_wrapper}`);
        this.socialMediaAnchors = this._element.querySelectorAll(`.${styles.social_media_button} a`);
        this.OGmetaTags = getOGMetaTags();
        Sharer.init(this.OGmetaTags);

        for (let shareButton of this.socialMediaAnchors) {
            shareButton.addEventListener('click', () => this.socialMediaClickHandler(shareButton))
        }
        this.nav_bg = this._element.querySelector(`#${styles.navi_list}`);
        this.hint_popup = this._element.querySelector(`#${styles.navi_hint}`);
        this.hint_text = this._element.querySelector('#navi_hint > .text');
        this.hint_close_button = this._element.querySelector('.hint_close');

        this.nav_cohort = this._element.querySelector('.li-cohorttable');
        this.nav_person = this._element.querySelector('.li-personcomparison');
        this.nav_region = this._element.querySelector('.li-regionalcomparison');

        this.hint_popup?.addEventListener('click', () => this.hideHintPopup())

    }

    getSizeFromBreakpoint() {
        let size;
        switch (ViewportUtility.breakpoint) {
            case 'w1024':
            case 'w880':
            case 'w750':
                size = 42;
                break;
            case 'w550':
                size = 38;
                break;
            default:
                size = 35;
        }
        return size;
    }

    public showHintPopup(delay = 0) {


        if (this.allowPopup && this.hint_popup && this.nav_cohort && this.nav_person) {
            this.hint_popup.style.pointerEvents = 'auto';

            this.timeline.add([
                gsap.to(this.hint_popup, 0.4, { delay: delay, autoAlpha: 1 }),
                gsap.fromTo(this.nav_cohort, 0.75,
                    {
                        backgroundColor: this.common.colors['cd-primary']
                    },
                    {
                        delay: delay + 1.5,
                        backgroundColor: 'transparent',
                        clearProps: "backgroundColor",
                        immediateRender: false,
                    }
                ),
                gsap.fromTo(this.nav_person, 0.75,
                    {
                        backgroundColor: this.common.colors['cd-primary']
                    },
                    {
                        delay: delay + 2.25,
                        clearProps: "backgroundColor",
                        backgroundColor: 'transparent',
                        immediateRender: false,
                    }
                ),
                /*gsap.fromTo(this.nav_region, 0.75,
                    {
                        backgroundColor: '#e5354d'
                    },
                    {
                        delay: delay + 2.5,
                        backgroundColor: '#ffffff',
                        immediateRender: false,
                    }
                ),*/
                gsap.set([this.nav_cohort, this.nav_person], {
                    clearProps: "backgroundColor",
                })

            ]);
        } else {
            this.allowPopup = true;
        }
    }

    hideHintPopup(delay = 0, duration = 0) {


        // when we navigate away from the current view, before tweens have been created;
        // they can't be prevented if they don't exist yet, so we lock it.
        this.allowPopup = false;

        // kill scheduled tweens if still in waiting/progress (to not show tweens after hiding them in a new view)
        if (this.hint_popup) {
            this.hint_popup.style.pointerEvents = 'none';

            gsap.killTweensOf(this.hint_popup);
            gsap.to(this.hint_popup, { duration: duration, delay: delay, autoAlpha: 0 })
        }
        if (this.nav_cohort) {
            gsap.killTweensOf(this.nav_cohort);
        }
        if (this.nav_person) {
            gsap.killTweensOf(this.nav_person);
        }


    }

    showShareButtons() {
        if (!this.socialMediaWrapper) {
            return;
        }
        gsap.killTweensOf(this.socialMediaWrapper);

        this.timeline.add([
            gsap.to(this.socialMediaWrapper, 0.3, { left: 30, autoAlpha: 1 })
        ]);
    }

    hideShareButtons() {
        if (!this.socialMediaWrapper) {
            return;
        }
        gsap.killTweensOf(this.socialMediaWrapper);

        this.timeline.add([
            gsap.to(this.socialMediaWrapper, 0.3, { left: -200, autoAlpha: 0 })
        ]);
    }
}
